import { useState, useEffect, useCallback } from 'react';
import { useServices } from '@/app/providers/ServiceContext';
import { useToast, getErrorMessage } from '@/app/providers';
import { useI18n } from '@/shared/i18n';
import type { CatalogGroup, ProviderConfigResponse, IntegrationTestResult } from '@yawi/types';

export function useIntegrationCatalog() {
  const { integrationCatalogRepo } = useServices();
  const toast = useToast();
  const { t } = useI18n();

  const [catalog, setCatalog] = useState<CatalogGroup[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [providerConfigs] = useState<Map<string, ProviderConfigResponse>>(() => new Map());

  const fetchCatalog = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await integrationCatalogRepo.getCatalog();
      setCatalog(response.groups);
    } catch (err) {
      const msg = getErrorMessage(err);
      setError(msg);
      toast.error(t('admin.settings.integrations.errorLoading'));
    } finally {
      setLoading(false);
    }
  }, [integrationCatalogRepo, toast, t]);

  useEffect(() => {
    fetchCatalog();
  }, [fetchCatalog]);

  const loadProviderConfig = useCallback(async (provider: string): Promise<ProviderConfigResponse | null> => {
    try {
      const config = await integrationCatalogRepo.getProviderConfig(provider);
      providerConfigs.set(provider, config);
      return config;
    } catch (err) {
      toast.error(getErrorMessage(err));
      return null;
    }
  }, [integrationCatalogRepo, providerConfigs, toast]);

  const saveProviderConfig = useCallback(async (
    provider: string,
    config: Record<string, unknown>,
    enabled?: boolean
  ): Promise<boolean> => {
    try {
      await integrationCatalogRepo.configureProvider(provider, { config, enabled });
      toast.success(t('admin.settings.integrations.saved'));
      await fetchCatalog();
      return true;
    } catch (err) {
      toast.error(t('admin.settings.integrations.errorSaving'));
      return false;
    }
  }, [integrationCatalogRepo, toast, t, fetchCatalog]);

  const toggleProvider = useCallback(async (
    provider: string,
    enabled: boolean,
    integrationId?: string
  ): Promise<boolean> => {
    try {
      if (!integrationId) {
        await integrationCatalogRepo.configureProvider(provider, { enabled });
      } else if (enabled) {
        await integrationCatalogRepo.enableIntegration(integrationId);
      } else {
        await integrationCatalogRepo.disableIntegration(integrationId);
      }
      await fetchCatalog();
      return true;
    } catch (err) {
      toast.error(getErrorMessage(err));
      return false;
    }
  }, [integrationCatalogRepo, toast, fetchCatalog]);

  const testConnection = useCallback(async (provider: string): Promise<IntegrationTestResult | null> => {
    try {
      const result = await integrationCatalogRepo.testProvider(provider);
      if (result.success) {
        toast.success(
          t('admin.settings.integrations.test.success').replace('{time}', String(result.responseTimeMs ?? 0))
        );
      } else {
        toast.error(
          t('admin.settings.integrations.test.failed').replace('{error}', result.error ?? 'Unknown error')
        );
      }
      return result;
    } catch (err) {
      toast.error(getErrorMessage(err));
      return null;
    }
  }, [integrationCatalogRepo, toast, t]);

  return {
    catalog,
    loading,
    error,
    fetchCatalog,
    loadProviderConfig,
    saveProviderConfig,
    toggleProvider,
    testConnection,
  };
}
